<template>
  <div class="integral-config-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('integralConfigManage.list.title')" :subtitle="$t('integralConfigManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.configName" clearable
            :placeholder="$t('integralConfigManage.search.configNamePlaceholder')"
            @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('integralConfigManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('integralConfigManage.page.empty')" @retry="getList">
        <el-table-column prop="configName" :label="$t('integralConfigManage.table.configName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="computingFormulaName" :label="$t('integralConfigManage.table.computingFormula')"
          align="center" min-width="160" show-overflow-tooltip />
        <el-table-column prop="squarePrice" :label="$t('integralConfigManage.table.squarePrice')" align="right" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.squarePrice }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="additionalAmount" :label="$t('integralConfigManage.table.additionalAmount')"
          align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.additionalAmount }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="scaleName" :label="$t('integralConfigManage.table.scale')" align="center" width="120" />
        <el-table-column :label="$t('common.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">{{ $t('common.edit') }}</el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 添加/编辑/删除组件 -->
    <add-integral-config ref="addDialog" @success="handleSuccess" />
    <edit-integral-config ref="editDialog" @success="handleSuccess" />
    <delete-integral-config ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { listIntegralConfig } from '@/api/scm/integralConfigManageApi'
import AddIntegralConfig from '@/components/scm/addIntegralConfig'
import EditIntegralConfig from '@/components/scm/editIntegralConfig'
import DeleteIntegralConfig from '@/components/scm/deleteIntegralConfig'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'IntegralConfigManageList',
  components: {
    AddIntegralConfig,
    EditIntegralConfig,
    DeleteIntegralConfig,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        configName: '',
        communityId: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await listIntegralConfig(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询积分标准失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('integralConfigManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.configName = ''
      this.page.current = 1
      this.getList()
    },
    handleAdd() {
      this.$refs.addDialog.open()
    },
    handleEdit(row) {
      this.$refs.editDialog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.integral-config-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
